import { useMutation, useQueryClient } from '@tanstack/react-query';

import { requestVersionsService } from '../../services/requestVersionsService';
import { offlineRequestsKeys } from '../queries/useOfflineRequestsQuery';
import { requestVersionKeys } from '../queries/useRequestVersionsQuery';

import type { RequestVersionRestoreRequest } from '../../types/RequestVersion';

interface RestoreVersionParams {
  versionId: number;
  body: RequestVersionRestoreRequest;
}

/**
 * Restore a request from a historical version. On success, invalidates the version list and
 * the request/child-config caches so the UI reflects the restored data and the new version.
 */
export const useRestoreVersion = (requestId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ versionId, body }: RestoreVersionParams) =>
      requestVersionsService.restoreVersion(requestId, versionId, body),
    onSuccess: () => {
      // A restore overwrites the whole request aggregate — parent fields, team, and
      // all six child collections — and records a new version. Rather than enumerate
      // every cache key (request detail, the 6 child-config roots, team under
      // offlineRequests, and the version list), invalidate everything so every open
      // view reflects the restored state. Restore is a rare, deliberate action, so the
      // extra refetches are acceptable.
      queryClient.invalidateQueries();
      // Explicit for clarity: the version list gained a new restore_* entry.
      queryClient.invalidateQueries({ queryKey: requestVersionKeys.all });
      // #1494: Restore runs from the Version History screen, so Request Detail is
      // unmounted and its query is INACTIVE — a bare invalidate (refetchType:'active')
      // marks it stale but never refetches it. And unlike the child-config queries
      // (which default to refetchOnMount:true and refresh on remount), the request
      // detail query `useOfflineRequestQuery` sets `refetchOnMount:false` to avoid
      // optimistic-edit races, so returning via "‹ Back to Request" (a remount) would
      // NOT refetch it either — leaving pre-restore parent fields until a full reload.
      // Force-refetch just that query now, including while inactive (`type:'all'`), so
      // the cache holds the restored request before the user navigates back. Not
      // awaited: the background refetch settles before/while the user returns, and the
      // detail query's `notifyOnChangeProps:'all'` re-renders it when it lands.
      void queryClient.refetchQueries({
        queryKey: offlineRequestsKeys.detail(String(requestId)),
        type: 'all',
      });
    },
    meta: { errorMessage: 'Failed to restore version. Please try again.' },
  });
};
